import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import { useMasterScheduleGridOptions } from './gridOptions'
import {
  generateGanttParameter,
  generateGanttScale,
} from '../../../../containers/commons/AgGrid/components/cell/custom/ganttReport/ganttUtil'
import DateVO from '../../../../../vo/DateVO'
import { useProjectPrivateContext } from '../../../../context/projectContext'

import { useMasterScheduleData } from './hooks/data'
import { useGridContext } from '../../../../containers/BulkSheetView/hooks/useGridContext'

type MasterScheduleProps = {
  projectUuid: string
  rootUuid: string | undefined
  targetUuids: string[]
  snapshotUuid: string
}
export const MasterSchedule = ({
  projectUuid,
  rootUuid,
  snapshotUuid,
  targetUuids,
}: MasterScheduleProps) => {
  const { project, wbsItemTypes } = useProjectPrivateContext()
  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const gridOptions = useMasterScheduleGridOptions()
  const { updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )
  const ref = useRef<HTMLDivElement>(null)
  const { rows } = useMasterScheduleData(projectUuid, snapshotUuid, targetUuids)
  const [ganttDisplayTerm, setGanttDisplayTerm] = useState<{
    start: DateVO
    end: DateVO
  }>({
    start: new DateVO(project.scheduledDate.startDate),
    end: new DateVO(project.scheduledDate.endDate),
  })
  const ganttParameter = useMemo(
    () => generateGanttParameter(ganttDisplayTerm),
    [ganttDisplayTerm]
  )

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api
    setIsGridReady(true)
  }, [])

  const initGanttParam = useCallback(async () => {
    const ganttTimeScale = await generateGanttScale(ganttParameter)
    updateContext({ ganttParameter, ganttTimeScale })
    gridApiRef.current?.refreshHeader()
    gridApiRef.current?.refreshCells({ columns: ['ganttChart'], force: true })
    gridApiRef.current?.setColumnWidths([
      { key: 'ganttChart', newWidth: ganttTimeScale.length * 40 },
    ])
  }, [ganttParameter, updateContext])

  useEffect(() => {
    if (!isGridReady) return
    initGanttParam()
  }, [initGanttParam, isGridReady])
  return (
    <>
      {rows.length > 0 && (
        <BulkSheetView
          ref={ref}
          gridOptions={gridOptions}
          rowData={rows}
          // Event
          onGridReady={e => {
            onGridReady(e)
            e.api.setColumnWidths([
              {
                key: 'ganttChart',
                newWidth:
                  (gridOptions?.context?.ganttTimeScale?.length ?? 0) * 40,
              },
            ])
          }}
        />
      )}
    </>
  )
}
